import mustache from "mustache";
import { IPage } from "../../shared/components/definitions/page/IPage";
import { CapacidadFincaCardRender } from "../../shared/components/modulos/pesaje/ganaderia/dashboard/CapacidadFincaCardRender";
import { LotesActivosTableRender } from "../../shared/components/modulos/pesaje/ganaderia/dashboard/LotesActivosTableRender";
import { PrecioMercadoStatRender } from "../../shared/components/modulos/pesaje/ganaderia/dashboard/PrecioMercadoStatRender";
import { ProyeccionSalidaCardRender } from "../../shared/components/modulos/pesaje/ganaderia/dashboard/ProyeccionSalidaCardRender";
import { ResultadoHistoricoCardRender } from "../../shared/components/modulos/pesaje/ganaderia/dashboard/ResultadoHistoricoCardRender";
import { SummaryItemRender } from "../../shared/components/implementations/summary/SummaryItemRender";
import { serviceProvider } from "../../shared/comun/provider/ServiceProvider";
import { fx_createCompraAnimalesGrpcService } from "../../shared/modulos/pesaje/ganaderia/services/CompraAnimalesGrpcService";
import { fx_createVentaAnimalesGrpcService } from "../../shared/modulos/pesaje/ganaderia/services/VentaAnimalesGrpcService";
import { fx_createLoteGrpcService } from "../../shared/modulos/terceros/services/LoteGrpcService";
import { _miles } from "../../shared/utils/numberUtils";
import template from "./pesajeGanaderiaDashboardTemplate.html";

mustache.parse(template);

/** Dashboard de Pesaje / Ganadería de la finca activa. En construcción: se
 *  arma sección por sección. */
export class PesajeGanaderiaDashboardPage implements IPage {
  private _precioMercado: PrecioMercadoStatRender | null = null;
  private _totalCabezas: SummaryItemRender | null = null;
  private _pesoTotal: SummaryItemRender | null = null;
  private _valorInventario: SummaryItemRender | null = null;
  private _capacidad: CapacidadFincaCardRender | null = null;
  private _lotesActivos: LotesActivosTableRender | null = null;
  private _proyeccionSalida: ProyeccionSalidaCardRender | null = null;
  private _resultadoHistorico: ResultadoHistoricoCardRender | null = null;
  private _disposed = false;

  render(
    pageContent: HTMLDivElement,
    _secondSidebarWrapper: HTMLDivElement,
  ): void {
    const finca = serviceProvider.getFincaSelectedService().getRequired();

    Promise.all([
      fx_createLoteGrpcService(finca.id).getListAsync(),
      fx_createCompraAnimalesGrpcService(finca.id).getListAsync(),
      fx_createVentaAnimalesGrpcService(finca.id).getListAsync(),
    ])
      .then(([lotes, compras, ventas]) => {
        if (this._disposed) return;

        const cabezasActivas = lotes.reduce(
          (sum, l) => sum + l.ganaderiaCantidadAnimales,
          0,
        );
        const potrerosActivos = lotes.filter(
          (l) => l.ganaderiaCantidadAnimales > 0,
        ).length;
        // ganaderiaPesoEstimadoTotalKg es el total del lote; ganaderiaPesoRealPromedioKg
        // es el promedio por animal — se multiplica por la cantidad de
        // animales para sumarlo como total.
        const pesoTotalKg = lotes.reduce((sum, l) => {
          if (l.ganaderiaPesoEstimadoTotalKg != null) {
            return sum + l.ganaderiaPesoEstimadoTotalKg;
          }
          if (l.ganaderiaPesoRealPromedioKg != null) {
            return sum + l.ganaderiaPesoRealPromedioKg * l.ganaderiaCantidadAnimales;
          }
          return sum;
        }, 0);
        const promedioKg = cabezasActivas > 0 ? pesoTotalKg / cabezasActivas : 0;
        const capacidadTotal =
          finca.ganaderiaAnimalesXHectareaOptimo * finca.hectareasProductivas;

        this._precioMercado = new PrecioMercadoStatRender();
        this._totalCabezas = new SummaryItemRender({
          icon: "ph-user",
          label: "Total cabezas en finca",
          description: `${potrerosActivos} potreros activos`,
          value: _miles(cabezasActivas),
        });
        this._pesoTotal = new SummaryItemRender({
          label: "Peso total estimado",
          description: `Promedio general: ${_miles(Math.round(promedioKg))} kg/animal`,
          value: `${_miles(Math.round(pesoTotalKg))} kg`,
        });
        this._valorInventario = new SummaryItemRender({
          label: "Valor inventario hoy",
          description: "Sin precio mercado ingresado",
          value: null,
        });
        this._capacidad = new CapacidadFincaCardRender(
          cabezasActivas,
          capacidadTotal,
        );
        this._lotesActivos = new LotesActivosTableRender(
          lotes,
          compras,
          finca.ganaderiaCrecimientoDiarioGr,
          finca.ganaderiaDiasOcupacionOptimo,
        );
        this._proyeccionSalida = new ProyeccionSalidaCardRender(lotes, compras);
        this._resultadoHistorico = new ResultadoHistoricoCardRender(compras, ventas);

        pageContent.innerHTML = mustache.render(template, {
          precioMercado: this._precioMercado.render(),
          totalCabezas: this._totalCabezas.render(),
          pesoTotal: this._pesoTotal.render(),
          valorInventario: this._valorInventario.render(),
          capacidad: this._capacidad.render(),
          lotesActivos: this._lotesActivos.render(),
          proyeccionSalida: this._proyeccionSalida.render(),
          resultadoHistorico: this._resultadoHistorico.render(),
        });

        this._precioMercado.connect();
        this._totalCabezas.connect();
        this._pesoTotal.connect();
        this._valorInventario.connect();
        this._capacidad.connect();
        this._lotesActivos.connect();
        this._proyeccionSalida.connect();
        this._resultadoHistorico.connect();

        const refreshValorInventario = () => {
          const precio = this._precioMercado?.getPrecio() ?? null;
          if (precio == null) {
            this._valorInventario?.setValue(null);
            this._valorInventario?.setDescription("Sin precio mercado ingresado");
            return;
          }
          this._valorInventario?.setValue(
            `$${_miles(Math.round(pesoTotalKg * precio))}`,
          );
          this._valorInventario?.setDescription(
            `a $${_miles(precio)}/kg precio mercado`,
          );
        };
        this._precioMercado.setOnChange(refreshValorInventario);
      })
      .catch((error) => {
        console.error("Error cargando el dashboard de pesaje/ganadería:", error);
        if (!this._disposed) {
          pageContent.innerHTML = `<div class="sx-empty">Error al cargar el dashboard. Revisa la consola.</div>`;
        }
      });
  }

  dispose(): void {
    this._disposed = true;
    this._precioMercado?.dispose();
    this._totalCabezas?.dispose();
    this._pesoTotal?.dispose();
    this._valorInventario?.dispose();
    this._capacidad?.dispose();
    this._lotesActivos?.dispose();
    this._proyeccionSalida?.dispose();
    this._resultadoHistorico?.dispose();
  }
}
